/**
 * Projects 项目管理页：项目列表（按状态筛选）+ 新建/编辑抽屉 + 完成/归档/删除。
 * 请求体严格白名单：CreateProjectBody 仅提交已填字段；UpdateProjectBody 可清空字段显式置 null。
 */
import { useState } from 'react';
import {
  useProjects, useCreateProject, useUpdateProject, useCompleteProject, useDeleteProject, useAreas,
} from '../hooks/useTasks';
import {
  Card, Button, Input, Textarea, Select, Badge, Spinner, ConfirmDialog, EmptyState, errMsg,
} from '../components/ui';
import { Icon } from '../components/icons';
import type {
  Project, ProjectStatus, CreateProjectBody, UpdateProjectBody,
} from '../lib/types';

// ProjectStatus 以 Prisma 枚举为准：ACTIVE / DONE / ARCHIVED（无 PLANNING/PAUSED）
const STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: 'ACTIVE', label: '进行中' },
  { value: 'DONE', label: '已完成' },
  { value: 'ARCHIVED', label: '已归档' },
];
const STATUS_LABELS: Record<ProjectStatus, string> = {
  ACTIVE: '进行中', DONE: '已完成', ARCHIVED: '已归档',
};
const STATUS_TONE: Record<ProjectStatus, string> = {
  ACTIVE: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300',
  DONE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  ARCHIVED: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

const EMPTY_FORM = {
  name: '', description: '', color: '', status: 'ACTIVE' as ProjectStatus,
  areaId: '', startDate: '', dueDate: '',
};

export function ProjectsPage() {
  const [filter, setFilter] = useState<'all' | ProjectStatus>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState<Project | null>(null);

  const { data: projects, isLoading, error } = useProjects(
    filter === 'all' ? { pageSize: 100 } : { status: filter, pageSize: 100 },
  );
  const { data: areas } = useAreas();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const completeProject = useCompleteProject();
  const deleteProject = useDeleteProject();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">项目</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            用项目组织跨多个任务的目标
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Icon name="plus" className="mr-1 h-4 w-4" />新建项目
        </Button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {(['all', ...STATUS_OPTIONS.map((s) => s.value)] as ('all' | ProjectStatus)[]).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === s
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
            }`}
          >
            {s === 'all' ? '全部' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {isLoading && <div className="flex justify-center py-12"><Spinner className="h-8 w-8" /></div>}
      {error && <Card><p className="text-sm text-red-600 dark:text-red-400">{errMsg(error)}</p></Card>}

      {!isLoading && !error && (projects?.items?.length ?? 0) === 0 && (
        <EmptyState
          icon={<Icon name="projects" className="mx-auto h-10 w-10" />}
          title="暂无项目"
          description="创建项目来组织你的事务"
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {(projects?.items ?? []).map((p) => (
          <Card key={p.id} className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                {p.color && (
                  <span
                    className="inline-block h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: p.color }}
                  />
                )}
                <h3 className="font-semibold text-slate-900 dark:text-white">{p.name}</h3>
              </div>
              <Badge className={STATUS_TONE[p.status]}>{STATUS_LABELS[p.status]}</Badge>
            </div>
            {p.description && (
              <p className="text-sm text-slate-500 line-clamp-2 dark:text-slate-400">{p.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              {p.areaId && <span>{(areas ?? []).find((a) => a.id === p.areaId)?.name ?? '领域'}</span>}
              {p.dueDate && <span>截止 {p.dueDate.slice(0, 10)}</span>}
            </div>
            <div className="mt-auto flex items-center gap-2 pt-1">
              {p.status !== 'DONE' && p.status !== 'ARCHIVED' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => completeProject.mutate(p.id)}
                  disabled={completeProject.isPending}
                >
                  <Icon name="check" className="mr-1 h-4 w-4" />完成
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setFormOpen(true); }}>
                <Icon name="edit" className="mr-1 h-4 w-4" />编辑
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 dark:text-red-400"
                onClick={() => setDeleting(p)}
              >
                <Icon name="trash" className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {formOpen && (
        <ProjectForm
          project={editing}
          onClose={() => setFormOpen(false)}
          createProject={createProject.mutateAsync}
          updateProject={updateProject.mutateAsync}
          pending={createProject.isPending || updateProject.isPending}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="删除项目"
        message={`确定删除项目「${deleting?.name}」吗？此操作不可恢复。`}
        confirmText="删除"
        danger
        busy={deleteProject.isPending}
        onConfirm={() => {
          if (!deleting) return;
          void deleteProject.mutateAsync(deleting.id).then(() => setDeleting(null)).catch(() => setDeleting(null));
        }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function ProjectForm({
  project, onClose, createProject, updateProject, pending,
}: {
  project: Project | null;
  onClose: () => void;
  createProject: (body: CreateProjectBody) => Promise<Project>;
  updateProject: (vars: { id: string; body: UpdateProjectBody }) => Promise<Project>;
  pending: boolean;
}) {
  const { data: areas } = useAreas();
  const [form, setForm] = useState(() => ({
    name: project?.name ?? EMPTY_FORM.name,
    description: project?.description ?? EMPTY_FORM.description,
    color: project?.color ?? EMPTY_FORM.color,
    status: project?.status ?? EMPTY_FORM.status,
    areaId: project?.areaId ?? EMPTY_FORM.areaId,
    startDate: project?.startDate ? project.startDate.slice(0, 10) : EMPTY_FORM.startDate,
    dueDate: project?.dueDate ? project.dueDate.slice(0, 10) : EMPTY_FORM.dueDate,
  }));
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) { setError('请填写项目名称'); return; }
    try {
      if (project) {
        // UpdateProjectBody：可清空字段显式置 null
        await updateProject({
          id: project.id,
          body: {
            name: form.name.trim(),
            description: form.description.trim() || null,
            color: form.color || null,
            status: form.status,
            areaId: form.areaId || null,
            startDate: form.startDate || null,
            dueDate: form.dueDate || null,
          },
        });
      } else {
        // CreateProjectBody：仅提交已填字段（严格白名单）
        const body: CreateProjectBody = { name: form.name.trim() };
        if (form.description.trim()) body.description = form.description.trim();
        if (form.color) body.color = form.color;
        body.status = form.status;
        if (form.areaId) body.areaId = form.areaId;
        if (form.startDate) body.startDate = form.startDate;
        if (form.dueDate) body.dueDate = form.dueDate;
        await createProject(body);
      }
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 shadow-xl dark:bg-slate-900 sm:rounded-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">{project ? '编辑项目' : '新建项目'}</h2>
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-400">
            {error}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">项目名称 *</label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">描述</label>
            <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">状态</label>
              <Select value={form.status} onChange={(e) => set('status', e.target.value as ProjectStatus)}>
                {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">领域</label>
              <Select value={form.areaId} onChange={(e) => set('areaId', e.target.value)}>
                <option value="">未分类</option>
                {(areas ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">颜色标记</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color || '#6366f1'}
                onChange={(e) => set('color', e.target.value)}
                className="h-9 w-14 cursor-pointer rounded border border-slate-300 bg-transparent dark:border-slate-600"
              />
              {form.color && (
                <Button variant="ghost" size="sm" type="button" onClick={() => set('color', '')}>
                  清除
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">开始日期</label>
              <Input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">截止日期</label>
              <Input type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>取消</Button>
          <Button type="submit" disabled={pending}>{pending ? '保存中…' : '保存'}</Button>
        </div>
      </form>
    </div>
  );
}
